'use client';
import React, { useState, useRef, useCallback } from 'react';

interface PasswordGateProps {
  onAuthenticated: () => void;
}

export default function PasswordGate({ onAuthenticated }: PasswordGateProps) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [shaking, setShaking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = useCallback(async () => {
    if (!password.trim() || isLoading) return;
    setIsLoading(true);
    setError('');
    try {
      // BACKEND INTEGRATION: POST /api/auth
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (data.success) {
        onAuthenticated();
      } else {
        setShaking(true);
        setError('Wrong password');
        setPassword('');
        setTimeout(() => setShaking(false), 400);
        inputRef.current?.focus();
      }
    } catch {
      setShaking(true);
      setError('Connection error — try again');
      setTimeout(() => setShaking(false), 400);
    } finally {
      setIsLoading(false);
    }
  }, [password, isLoading, onAuthenticated]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center" style={{ backgroundColor: 'var(--bg)' }}>
      <div className="dot-grid-bg fixed inset-0 pointer-events-none" />
      <div className="relative z-10 flex flex-col items-center" style={{ gap: '36px' }}>
        {/* Logo block */}
        <div className="flex flex-col items-center gap-2">
          <span className="font-mono text-text2 tracking-[4px] text-[13px]">ORBITAL</span>
          <span className="font-mono font-bold text-green text-[28px] leading-none">Niche</span>
          <span className="font-mono text-text3 text-[12px] mt-1">Vietnam Intelligence // v10</span>
        </div>

        {/* Input row */}
        <div className={`flex gap-2 w-72 ${shaking ? 'shake' : ''}`}>
          <input
            ref={inputRef}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter password"
            autoFocus
            className="input-field flex-1"
            style={{ fontFamily: 'var(--font-mono)' }}
            disabled={isLoading}
          />
          <button
            onClick={handleSubmit}
            disabled={isLoading || !password.trim()}
            className="btn-primary px-4 py-2 font-mono text-[13px] tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center gap-1">
                <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31" strokeDashoffset="10" />
                </svg>
              </span>
            ) : 'ENTER'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <span className="font-mono text-red text-[12px]">{error}</span>
        )}
      </div>
    </div>
  );
}